"use client";
import { useState, useEffect } from 'react';
import { trackEngagement } from '@/lib/analytics';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  className?: string;
}

export default function TableOfContents({ className = '' }: TableOfContentsProps) {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Only look for headings within the article content area
    // This excludes headings from page layout, CTAs, related articles, etc.
    const articleContainer = document.querySelector('article') || 
                            document.querySelector('.prose') || 
                            document.querySelector('[class*="prose"]');
    
    if (!articleContainer) {
      console.warn('No article container found for Table of Contents');
      return;
    }

    // Generate table of contents from headings within the article only
    const headings = articleContainer.querySelectorAll('h2, h3, h4');
    const items: TocItem[] = [];

    headings.forEach((heading, index) => {
      // Skip headings that are in CTA sections or other non-content areas
      const isInCTA = heading.closest('.bg-gray-50') || 
                     heading.closest('[class*="bg-gray"]') ||
                     heading.closest('.cta') ||
                     heading.closest('[class*="cta"]');
      
      if (isInCTA) {
        return; // Skip this heading
      }

      const id = heading.id || `heading-${index}`;
      if (!heading.id) {
        heading.id = id;
      }

      const text = heading.textContent || '';
      
      // Additional filter to exclude common non-content headings
      const excludePatterns = [
        /ready to/i,
        /share this/i,
        /related articles/i,
        /stay updated/i,
        /newsletter/i,
        /subscribe/i,
        /contact/i,
        /get started/i,
        /learn more/i,
        /explore/i,
        /discover/i
      ];
      
      const shouldExclude = excludePatterns.some(pattern => pattern.test(text));
      if (shouldExclude) {
        return; // Skip this heading
      }

      items.push({
        id,
        text,
        level: parseInt(heading.tagName.charAt(1))
      });
    });

    setTocItems(items);

    // Set up intersection observer for active section tracking
    // Only observe headings that are in our TOC
    const observableHeadings = items.map(item => document.getElementById(item.id)).filter(Boolean);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0% -35% 0%',
        threshold: 0
      }
    );

    observableHeadings.forEach((heading) => {
      if (heading) observer.observe(heading);
    });

    return () => {
      observableHeadings.forEach((heading) => {
        if (heading) observer.unobserve(heading);
      });
    };
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, text: string) => {
    e.preventDefault();
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      // Track engagement
      trackEngagement('toc_click', 1);
      
      // Track specific section for content optimization
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'toc_section_click', {
          event_category: 'Content Navigation',
          event_label: text,
          custom_parameter_section_id: id
        });
      }
    }
  };

  if (tocItems.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white border border-gray-100 rounded-lg shadow-sm ${className}`}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4 mr-2 text-gray-500" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          <span className="text-sm font-medium text-gray-900">Table of Contents</span>
          <span className="text-xs text-gray-500 ml-2">({tocItems.length} sections)</span>
        </div>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <nav className="space-y-1">
            {tocItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id, item.text)}
                className={`
                  block text-sm py-1 px-2 rounded transition-colors duration-200 hover:bg-gray-50 hover:text-[#00A4BD]
                  ${item.level === 2 ? 'font-medium' : ''}
                  ${item.level === 3 ? 'ml-3 text-gray-600' : ''}
                  ${item.level === 4 ? 'ml-6 text-gray-500' : ''}
                  ${activeId === item.id ? 'text-[#00A4BD] bg-[#E6F7F9] font-medium' : 'text-gray-700'}
                `}
              >
                {item.text}
              </a>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}